import { handleUnauthorizedResponse } from "./auth-status";
import { refreshSession, stopSession } from "./session";
import { getAccessToken, isRefreshDue } from "./session-store";

const AUTH_API_PREFIX = "/api/v1/auth/";
const PROFILE_PATH = "/api/v1/auth/me/";
const REPLAYABLE_METHODS = new Set(["GET", "HEAD", "OPTIONS", "PUT", "DELETE"]);

function usesAccessToken(request: Request): boolean {
  const path = new URL(request.url).pathname;
  return path === PROFILE_PATH || !path.startsWith(AUTH_API_PREFIX);
}

function withBearer(request: Request, accessToken: string): Request {
  const headers = new Headers(request.headers);
  headers.set("Authorization", `Bearer ${accessToken}`);
  return new Request(request, { headers });
}

async function sendWithAccessToken(request: Request): Promise<Response> {
  if (isRefreshDue()) {
    await refreshSession();
  }
  const sentToken = getAccessToken();
  if (sentToken === null) {
    return fetch(request);
  }

  const replay = REPLAYABLE_METHODS.has(request.method) ? request.clone() : null;
  const response = await fetch(withBearer(request, sentToken));
  if (response.status !== 401) {
    return response;
  }

  if (getAccessToken() === sentToken) {
    const outcome = await refreshSession();
    if (outcome !== "refreshed") {
      return response;
    }
  }

  const freshToken = getAccessToken();
  if (replay === null || freshToken === null) {
    return response;
  }

  const retried = await fetch(withBearer(replay, freshToken));
  if (retried.status === 401) {
    stopSession();
  }
  return retried;
}

export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const request = new Request(input, init);
  if (!usesAccessToken(request)) {
    return fetch(request);
  }

  const response = await sendWithAccessToken(request);
  if (response.status === 401) {
    handleUnauthorizedResponse();
  }
  return response;
}
